import React from 'react';
import { PropTypes } from 'prop-types';
import TextField from '@mui/material/TextField';
import { Controller, get } from 'react-hook-form';

import useRemoveSpaces from '../utils/removeSpaces';
import getFormControllerRules from '../utils/getFormControllerRules';

/**
 * Reusable TextField for use with react-hook-form.
 *
 * @param {object} control - React hook form control.
 * @param {object} formErrors - React hook form formErrors.
 * @param {string} label - Label to be used for the field as well as test id and error messages.
 * @param {string} formFieldId - Id to be used with react-hook-form.
 * @param {boolean} required - Is the field required?
 * @param {boolean} readonly - Is the field read only?
 * @param {boolean} showInlineErrors - Should the field show errors in help text? (defaults to `true`)
 * @param {object} rest - Also accepts all TextField props.
 * @param {string} testId - Alternative testId label to overwrite label.
 * @param {object} validationOptions - React Hook Form validation options.
 * @param {func} onAdditionalMethod - Additional method when onChange is fired.
 * @param {string} defaultValue
 */
const FormTextField = ({
  control,
  formErrors,
  label,
  formFieldId,
  readonly,
  required,
  testId,
  validationOptions,
  helperText,
  showInlineErrors = true,
  inputProps,
  onAdditionalMethod,
  defaultValue,
  ...rest
}) => {
  const testIdMemo = useRemoveSpaces(testId ?? `${ label }_Field`);
  const rules = getFormControllerRules(label, required, validationOptions);
  const modifiedHelperText = (showInlineErrors && get(formErrors, formFieldId)?.message) || helperText;
  const parseValue = (value) => {
    if (rest.type === 'number') {
      return value ? parseFloat(value) : '';
    }
    return value;
  };

  return (
    <Controller
      name={formFieldId}
      control={control}
      rules={!readonly ? rules : undefined}
      defaultValue={defaultValue}
      render={({
        field: {
          ref, onChange, value
        }
      }) => (
        <TextField
          disabled={readonly}
          inputRef={ref}
          sx={{ overflow: 'visible' }}
                //   variant={readonly ? 'filled' : 'outlined'}
          required={required}
          fullWidth
          helperText={modifiedHelperText}
          label={label}
          error={!!get(formErrors, formFieldId)}
          inputProps={{ 'data-testid': testIdMemo, ...inputProps }}
          value={value ?? ''}
          onChange={(e) => {
            onChange(parseValue(e.target.value));
            // eslint-disable-next-line no-unused-expressions
            onAdditionalMethod && onAdditionalMethod(e);
          }}
          {...rest}
        />
      )}
    />
  );
};

FormTextField.propTypes = {
  control: PropTypes.object.isRequired,
  formErrors: PropTypes.object,
  label: PropTypes.string.isRequired,
  formFieldId: PropTypes.string.isRequired,
  required: PropTypes.bool,
  readonly: PropTypes.bool,
  testId: PropTypes.string,
  validationOptions: PropTypes.object,
  helperText: PropTypes.string,
  defaultValue: PropTypes.string,
  showInlineErrors: PropTypes.bool,
  inputProps: PropTypes.object,
  onAdditionalMethod: PropTypes.func
};

FormTextField.defaultProps = {
  formErrors: null,
  required: false,
  readonly: false,
  testId: null,
  validationOptions: {},
  helperText: null,
  showInlineErrors: true,
  onAdditionalMethod: null,
  inputProps: null,
  defaultValue: undefined
};

export default FormTextField;
